import { OutcomeType } from "components/server-types";
import { t } from "i18n/i18n";
import { RootState } from "store/index";
import {
  outcomeSelectors,
  pageOutcomeSelectors,
  sectionOutcomeSelectors,
  selectNestedPageOutcomes,
  selectOrgOutcomeRubric,
  selectPageOutcomeRubric,
  selectSectionOutcomeRubric,
} from "store/selectors";

export const OUTCOME_SYSTEM_EXPLANATION_CONTEXT = [
  `Forum supports defining learning outcomes on the Organizational, Section, and Page levels,`,
  `and the rubric description for each outcome can be defined at each level, for each score.`,
  `Outcomes have a title and description, and a rubric, which is a list of a title and description`,
  `pair for each distinct score that can be given.`,
  `When looking at a Page, the rubric will only show the rubric descriptions if they have been`,
  `specifically defined for that Page, otherwise it will show the rubric title from its parent scope,`,
  `and not show the rubric description.`,
  `Outcome titles are generally referenced with a hash symbol, like #<outcome-1>.`,
].join("\n");

export const OUTCOME_DEVELOPMENT_BEST_PRACTICES_SYSTEM_PROMPT = [
  "Up to eight Section Outcomes may be linked to a specific Page, though most pages should focus on one or two outcomes,",
  "with Assignments representing bigger work products that integrate across multiple outcomes.",
  "Outcomes should be relevant to the existing Page content and surrounding content (if available).",
  "Pages should generally adapt and specify the generic section-level rubrics of the outcomes tagged on the Page.",
  "Every Outcome should have a specific rubric description for every score in the scoring range,",
  "as it applies to the current Page and its context in the section, which makes it easy for a grader to determine",
  "the correct score for a given user submission based on the rubric, and should make it easy for the student to understand",
  "the expectations on their work for the current Page, without giving away examples or hints,",
  "and the rubric should not bias students viewing it.",
  "Non-assignment Pages should generally contain one to three exercises, and should not take up more than 10 minutes to complete.",
  "Content of Pages should be aligned with the outcomes tagged on the Page, and fit into its surrounding Pages, Activities, and Topics.",
].join("\n");

/**
 * Given a list of outcomes, returns "outcomes #<name1>, #<name2>" or "no outcomes"
 */
export const selectOutcomeStatusLine = (outcomes: OutcomeType[]) => {
  if (outcomes.length === 0) {
    return "no outcomes";
  }
  return `outcomes ${outcomes.map((o) => `#${o.title}`).join(", ")}`;
};

/**
 * Outputs either "No outcomes are tagged on the current Page"
 * or a list of outcomes tagged on the Page. If the outcomes have descriptions,
 * the description will be included.
 * The rubric will be included, in short form mentioning "inherited from section level",
 * unless the rubric has been specified _specifically_ for the Page.
 *
 * Example:
 * The following outcomes are tagged on the current Page:
 *   - #<outcome-1>: <description>
 *     - 1: Basic Competency (inherited from section level)
 *     - 2: Advanced Competency: (<rubric>)
 *   - #<outcome-2>: <description>
 *     - 1: Basic Competency (inherited from section level)
 *     - 2: Advanced Competency: (inherited from section level)
 */
// TODO: All the functions in this file have similar ways of formatting outcomes. Unify.
export const selectPageOutcomeContext = (state: RootState, pageId: string) => {
  const outcomes = outcomeSelectors.selectAll(state);
  const pageOutcomes = pageOutcomeSelectors.filterByPageId(state, pageId);
  const sectionOutcomes = sectionOutcomeSelectors.selectAll(state);
  const nestedPageOutcomes = selectNestedPageOutcomes(state, pageId);

  const usedOutcomes = Object.values(outcomes).filter((o) =>
    pageOutcomes.some((po) =>
      sectionOutcomes.some((co) => co.outcome_id === o.id && po.section_outcome_id === co.id),
    ),
  );

  const PAGE_OUTCOME_CONTEXT = usedOutcomes
    .map((o) => {
      const pageOutcome = nestedPageOutcomes.find((npo) => npo.outcome.id === o.id);
      let outcomeText = o.description ? `- #${o.title}: ${o.description}` : `- #${o.title}`;

      if (pageOutcome) {
        const rubric = selectPageOutcomeRubric(state, pageOutcome.id);
        const rubricLines = rubric.map((rubricLevel) => {
          const isInherited = !pageOutcome.rubric_descriptions;
          const summary = `    - ${rubricLevel.score}: ${rubricLevel.name}`;
          return isInherited ? summary : `${summary}: ${rubricLevel.description}`;
        });
        outcomeText += `\n${rubricLines.join("\n")}`;
      }

      return outcomeText;
    })
    .join("\n");

  if (PAGE_OUTCOME_CONTEXT.length === 0) {
    return t("ai.page_no_outcomes_available");
  }

  return t("ai.page_outcomes_available", { outcomes: PAGE_OUTCOME_CONTEXT });
};

/**
 * Outputs either "No outcomes are available in this section"
 * or a list of outcomes available in the section. If the outcomes have descriptions,
 * the description will be included. If "includeRubric" is true, the rubric will be included,
 * in short form mentioning "inherited from section level",
 * unless the rubric has been specified _specifically_ for the section.
 *
 * Example:
 * The following outcomes are available in this section:
 *   - #<outcome-1>: <description>
 *     - 1: Basic Competency (inherited from section level)
 *     - 2: Advanced Competency: (<rubric>)
 *   - #<outcome-2>: <description>
 *     - 1: Basic Competency (inherited from section level)
 *     - 2: Advanced Competency: (inherited from section level)
 */
export const selectCurrentSectionOutcomeContext = (state: RootState) => {
  const outcomes = outcomeSelectors.selectAll(state);
  const sectionOutcomes = sectionOutcomeSelectors.selectAll(state);
  const availableOutcomes = Object.values(outcomes).filter((o) =>
    sectionOutcomes.some((co) => co.outcome_id === o.id),
  );

  const SECTION_OUTCOME_CONTEXT = availableOutcomes
    .map((o) => {
      const sectionOutcome = sectionOutcomes.find((co) => co.outcome_id === o.id);
      let outcomeText = o.description ? `- #${o.title}: ${o.description}` : `- #${o.title}`;

      if (sectionOutcome) {
        const rubric = selectSectionOutcomeRubric(state, sectionOutcome.id);
        if (!rubric) {
          return outcomeText;
        }
        const rubricLines = rubric.map((rubricLevel) => {
          const isInherited = !sectionOutcome.rubric_descriptions;
          const summary = `    - ${rubricLevel.score}: ${rubricLevel.name}`;
          return isInherited ? summary : `${summary}: ${rubricLevel.description}`;
        });
        outcomeText += `\n${rubricLines?.join("\n")}`;
      }

      return outcomeText;
    })
    .join("\n");

  if (SECTION_OUTCOME_CONTEXT.length === 0) {
    return t("ai.section_no_outcomes_available");
  }

  return t("ai.section_outcomes_available", { outcomes: SECTION_OUTCOME_CONTEXT });
};

/**
 * Outputs either "No outcomes are available in this organization"
 * or a list of outcomes available in the organization. If the outcomes have descriptions,
 * the description will be included. If "includeRubric" is true, the rubric will be included,
 * in short form mentioning "inherited from org level",
 * unless the rubric has been specified _specifically_ for the organization.
 *
 * Example:
 * The following outcomes are available in this organization:
 *   - #<outcome-1>: <description>
 *     - 1: Basic Competency
 *     - 2: Advanced Competency: (<rubric>)
 *   - #<outcome-2>: <description>
 *     - 1: Basic Competency (default)
 *     - 2: Advanced Competency: (default)
 */
// TODO: Ensure the "(default)" text is added correctly for non-customized rubrics.
export const selectCurrentOrgOutcomeContext = (
  state: RootState,
  { includeRubric = false }: { includeRubric?: boolean } = {},
) => {
  const outcomes = outcomeSelectors.selectAll(state);

  const ORG_OUTCOME_CONTEXT = Object.values(outcomes)
    .map((o) => {
      let outcomeText = o.description ? `- #${o.title}: ${o.description}` : `- #${o.title}`;

      if (includeRubric) {
        const rubric = selectOrgOutcomeRubric(state, o.id);
        const rubricLines = rubric.map((rubricLevel) => {
          const isInherited = !o.rubric_descriptions;
          const summary = `    - ${rubricLevel.score}: ${rubricLevel.name}`;
          return isInherited ? summary : `${summary}: ${rubricLevel.description}`;
        });
        outcomeText += `\n${rubricLines.join("\n")}`;
      }

      return outcomeText;
    })
    .join("\n");

  if (ORG_OUTCOME_CONTEXT.length === 0) {
    return t("ai.org_no_outcomes_available");
  }

  return t("ai.org_outcomes_available", { outcomes: ORG_OUTCOME_CONTEXT });
};
